import { useMemo } from "react";
import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  boomCrmLinks,
  boomReportLinks,
  boomScreenLinks,
  collectionsLinks,
  globalLinks,
  Links,
  listingFraudAlertsLink,
  NavItemId,
  NavLinkConfig,
  NavSectionId,
  renewalsLink
} from "./constants";
import { shouldShowGlobalLink, shouldShowReportingLinks } from "./utils";
import { canViewCallsReport } from "views/calls/lib/access";
import { isNotificationsForAllPartners } from "views/boom-notifications/lib/access";

export interface NavSection {
  id: NavSectionId;
  title?: string;
  isNew?: boolean;
  items: NavLinkConfig[];
}

const collectionsSectionItems = (
  {
    isCollectionsOutstandingBalance,
    isRenewals
  }: { isCollectionsOutstandingBalance: boolean; isRenewals: boolean },
  callsLink: NavLinkConfig[]
): NavLinkConfig[] => [
  ...(isCollectionsOutstandingBalance ? collectionsLinks : []),
  ...(isRenewals ? [renewalsLink] : []),
  ...callsLink
];

export const usePermittedNavSections = (): NavSection[] => {
  const { type, permissions, auth, partner, pms } = useAuthContext();
  const { Units, Properties, Property } = useUnitsPropertiesDictionary();

  return useMemo(() => {
    const sections: NavSection[] = [];
    const showsGlobalSection = type.isBoomScreen || type.isRentReporting;
    // Calls lives in the global section. A partner without it still gets
    // Calls, at the end of its BoomCRM section, or of its Collections section
    // when it has no CRM (CRM-1472).
    const strandedCallsLink = globalLinks.filter(
      ({ id }) =>
        id === NavItemId.CALLS &&
        !showsGlobalSection &&
        canViewCallsReport(type)
    );
    const crmCallsLink = type.isBoomCRM ? strandedCallsLink : [];
    const collectionsCallsLink = type.isBoomCRM ? [] : strandedCallsLink;

    if (showsGlobalSection) {
      const nameOverrides: Record<string, string> = {
        [Links.UNITS]: Units,
        [Links.PROPERTIES]: Properties,
        [Links.PROPERTY_GROUPS]: `${Property} groups`
      };

      sections.push({
        id: NavSectionId.GLOBAL,
        items: globalLinks
          .filter(({ name }) => shouldShowGlobalLink(name, type))
          .map((link) => ({
            ...link,
            name: nameOverrides[link.name] || link.name
          }))
      });
    }
    // Boom network rental verifications reach any partner (BSCPF-5344), so
    // Notifications shows without the rest of the global links (BSCPF-6947).
    else if (isNotificationsForAllPartners(partner))
      sections.push({
        id: NavSectionId.GLOBAL,
        items: globalLinks.filter(({ id }) => id === NavItemId.NOTIFICATIONS)
      });

    if (type.isBoomCRM)
      sections.push({
        id: NavSectionId.CRM,
        title: "BoomCRM",
        isNew: true,
        items: [
          ...boomCrmLinks.filter(
            ({ id }) => id !== NavItemId.MARKET_RENT || type.isMarketRent
          ),
          ...crmCallsLink,
          ...(type.isListingFraudAlerts ? [listingFraudAlertsLink] : [])
        ]
      });

    if (type.isListingFraudAlerts && !type.isBoomCRM)
      sections.push({
        id: NavSectionId.LISTINGS,
        title: "Listings",
        items: [listingFraudAlertsLink]
      });

    if (type.isBoomScreen)
      sections.push({
        id: NavSectionId.SCREEN,
        title: "BoomScreen",
        items: boomScreenLinks
          .filter(({ name }) => {
            if (name === Links.SCREENING_OUTCOMES)
              return type.isBoomScreenOutcomes && type.adminPrivileges;

            if (type.isPropertyManager && name === Links.SCREENING_SETTINGS)
              return (
                auth?.can_access_screening_templates ||
                permissions.canManageDiscounts
              );

            return true;
          })
          .map((link) =>
            link.name === Links.SCREENING_SETTINGS &&
            type.isPropertyManager &&
            !auth?.can_access_screening_templates &&
            permissions.canManageDiscounts
              ? { ...link, to: "/discounts" }
              : link
          )
      });

    if (type.isCollectionsOutstandingBalance || type.isRenewals)
      sections.push({
        id: NavSectionId.COLLECTIONS,
        title: "Collections",
        items: collectionsSectionItems(type, collectionsCallsLink)
      });

    if (type.isRentReporting || type.isSDK) {
      const hasIdentityTheftProtection = Boolean(
        partner?.features.includes(FEATURES.ENABLE_IDENTITY_THEFT_PROTECTION)
      );

      sections.push({
        id: NavSectionId.REPORT,
        title: hasIdentityTheftProtection ? "Resident benefits" : "BoomReport",
        items: boomReportLinks.filter(
          ({ name, permission }) =>
            shouldShowReportingLinks(name, type, partner, pms) &&
            permissions[permission as keyof typeof permissions] &&
            (name !== Links.IDENTITY_THEFT_PROTECTION ||
              hasIdentityTheftProtection)
        )
      });
    }

    return sections.filter(({ items }) => items.length > 0);
  }, [type, permissions, auth, partner, pms, Units, Properties, Property]);
};
